// httppackmanager.ts ↔ mpshared/packmanager.cpp (PackManager — the installed-
// pack map over the cache directory) + game/httppackmanager.cpp
// (HttpPackManager — the HTTP download/install arm, gppackm).
//
// SHAPE NOTES (the C++→TS seams):
//  1. The POSIX cache directory (opendir/readdir, unlink, mkstemp+rename) is an
//     injected PackStore: keys are the C++ cache FILENAMES
//     ("iiiiiiii-hhhhhhhh…32", PackManager::GetPackFilename), values the pack
//     bytes. MemoryPackStore is the default; the browser shell will back the
//     same interface with IndexedDB (hydrated to memory at boot — the C++ API
//     is synchronous, so writes flush through asynchronously underneath).
//  2. The download temp file (mkstemp + fwrite + rename-into-cache) collapses
//     to an in-memory chunk list; FinishInstall's fseek/fread checks run over
//     the assembled bytes. The bytes that land in the store are identical.
//  3. Mount/Unmount: the C++ pushes the pack FILE onto the PackFileReader
//     (pakr.Push(NULL, filepath)); here a MemPdbReader opens over the stored
//     bytes and is pushed under the same dir/filename split.
//
// PackManager::WatchIndex/WatcherStart/OnMessage/LoadIndex are the SERVER's
// index-watch arm (server/main.cpp:162 — a watcher thread over the index
// file's mtime); no client build calls them — not ported.

import { MemPdbReader } from '../io/pdbreader.ts';
import type { PackFileReader } from '../io/packfile.ts';
import { PACKID_MAIN, type PackId } from '../io/savegame.ts';
import { md5bytes } from '../../../workers/leaderboard/src/md5.ts';
import {
  ToHex,
  FromHex,
  ToDword,
  ToInteger,
  kszPackUrl,
  kdwClientID,
  knVersionSimulation,
  HeadersMap,
  HttpRequest,
  type HttpService,
  type HttpResponseHandler,
  type ProgressCallback,
} from './httpservice.ts';

// mpshared/mpht.h:18 — the addon-pack pdb type ("ADD2" at file offset 0x3c)
export const kszTypeAddon = 'ADD2';

/** The cache-directory seam (see SHAPE NOTE 1). */
export interface PackStore {
  list(): string[];
  read(filename: string): Uint8Array | null;
  write(filename: string, bytes: Uint8Array): void;
  remove(filename: string): boolean;
}

export class MemoryPackStore implements PackStore {
  private files = new Map<string, Uint8Array>();
  list(): string[] {
    return [...this.files.keys()];
  }
  read(filename: string): Uint8Array | null {
    return this.files.get(filename) ?? null;
  }
  write(filename: string, bytes: Uint8Array): void {
    this.files.set(filename, bytes);
  }
  remove(filename: string): boolean {
    return this.files.delete(filename);
  }
}

// the C++ memcmp(ppackid, &it->second, sizeof(*ppackid)) over {dword id;
// byte hash[16]}
function PackIdsEqualRaw(a: PackId, b: PackId): boolean {
  if (a.id >>> 0 !== b.id >>> 0) return false;
  for (let i = 0; i < 16; i++) {
    if ((a.hash[i] ?? 0) !== (b.hash[i] ?? 0)) return false;
  }
  return true;
}

const clonePackId = (p: PackId): PackId => ({ id: p.id >>> 0, hash: p.hash.slice() });

//
// PackManager — mpshared/packmanager.cpp
//

export class PackManager {
  protected cachedir_: string;
  protected readonly store: PackStore;
  // std::map<dword,PackId> — iteration order is id-ascending (EnumPacks sorts)
  protected map_ = new Map<number, PackId>();
  // Mount pushes a fresh MemPdbReader per packid; Unmount pops it (packfile's
  // Pop closes the reader)
  private mounted: MemPdbReader[] = [];

  // mpshared/packmanager.cpp:16-18
  constructor(store: PackStore, cachedir = 'packs') {
    this.store = store;
    this.cachedir_ = cachedir;
  }

  // mpshared/packmanager.cpp:23-37 — the Enum cursor collapses to an array
  // (std::map iterates keys ascending; mirrored so install-state sorting and
  // MergeInstalled see the same order)
  EnumPacks(): PackId[] {
    return [...this.map_.entries()]
      .sort((a, b) => (a[0] >>> 0) - (b[0] >>> 0))
      .map(([, packid]) => clonePackId(packid));
  }

  // mpshared/packmanager.cpp:39-64 — 0 = not installed, 1 = exact pack,
  // 2 = same id but different hash (upgrade); ppackidUpgrade receives the
  // installed pack in case 2 (mutated in place, the C++ out param)
  IsInstalled(ppackid: PackId, ppackidUpgrade: PackId | null = null): number {
    // This is temporary. Ideally, the "main" packs use the same
    // versioning system.
    if (ppackid.id >>> 0 === PACKID_MAIN) {
      // Exact pack found
      return 1;
    }

    // This needs to be fast, so a map is used.
    const it = this.map_.get(ppackid.id >>> 0);
    if (it === undefined) {
      // Not found
      return 0;
    }
    if (PackIdsEqualRaw(ppackid, it)) {
      // Exact pack found
      return 1;
    }

    // Pack found, but needs upgrade (hash mismatch)
    if (ppackidUpgrade !== null) {
      ppackidUpgrade.id = it.id >>> 0;
      ppackidUpgrade.hash.set(it.hash.subarray(0, 16));
    }
    return 2;
  }

  // mpshared/packmanager.cpp:66-91
  Remove(ppackid: PackId, fCheckHash = true): boolean {
    const result = this.IsInstalled(ppackid);
    if (result === 0) return false;
    if (fCheckHash && result === 2) return false;

    const it = this.map_.get(ppackid.id >>> 0);
    if (it === undefined) {
      // It should always be found at this point (Assert())
      return false;
    }

    // Remove the file (unlink(GetPackFilepath) — the store key is the filename)
    if (!this.store.remove(PackManager.GetPackFilename(it))) {
      // This shouldn't fail (Assert())
      return false;
    }

    this.map_.delete(ppackid.id >>> 0);
    return true;
  }

  // mpshared/packmanager.cpp:93-98 — pakr.Push(NULL, GetPackFilepath) over the
  // stored bytes (SHAPE NOTE 3)
  Mount(pakr: PackFileReader, ppackid: PackId): boolean {
    if (ppackid.id >>> 0 === PACKID_MAIN) return true;
    const bytes = this.store.read(PackManager.GetPackFilename(ppackid));
    if (bytes === null) return false;
    const rdr = new MemPdbReader();
    if (!rdr.Open(bytes)) return false;
    if (!pakr.Push(null, this.GetPackFilepath(ppackid), rdr)) return false;
    this.mounted.push(rdr);
    return true;
  }

  // mpshared/packmanager.cpp:100-105
  Unmount(pakr: PackFileReader, ppackid: PackId): boolean {
    if (ppackid.id >>> 0 === PACKID_MAIN) return true;
    this.mounted.pop();
    return pakr.Pop();
  }

  // mpshared/packmanager.cpp:107-119 — opendir/readdir over the store
  InitFromInstalled(): void {
    this.map_.clear();
    for (const filename of this.store.list()) {
      const packid: PackId = { id: 0, hash: new Uint8Array(16) };
      if (!this.ParseFilename(filename, packid)) continue;
      // std::map::insert keeps the FIRST entry per id — mirrored
      if (!this.map_.has(packid.id >>> 0)) {
        this.map_.set(packid.id >>> 0, packid);
      }
    }
  }

  // mpshared/packmanager.cpp:121-125 — "%08x-%s" (id hex8, md5 hex32)
  static GetPackFilename(ppackid: PackId): string {
    const szHash = ToHex(ppackid.hash, 16);
    return `${(ppackid.id >>> 0).toString(16).padStart(8, '0')}-${szHash}`;
  }

  // mpshared/packmanager.cpp:127-131 — "%s/%s" (cachedir/filename)
  protected GetPackFilepath(ppackid: PackId): string {
    return `${this.cachedir_}/${PackManager.GetPackFilename(ppackid)}`;
  }

  // mpshared/packmanager.cpp:133-145 — iiiiiiii-hhhh…32; NOTE the C++ never
  // verifies the '-' separator (preserved)
  protected ParseFilename(filename: string, ppackid: PackId): boolean {
    if (filename.length !== 8 + 1 + 32) return false;
    const id = ToDword(filename.slice(0, 8), 16);
    if (id === null) return false;
    ppackid.id = id;
    return FromHex(filename.slice(8 + 1), ppackid.hash, 16);
  }
}

//
// HttpPackManager — game/httppackmanager.cpp (gppackm)
//

export class HttpPackManager extends PackManager implements HttpResponseHandler {
  private service_: HttpService | null;
  private ctx_: unknown = null;
  private callback_: ProgressCallback | null = null;
  private req_: HttpRequest | null = null;
  private code_ = 0;
  private error_ = false;
  private cbTotal_ = 0;
  private cbLength_ = 0;
  // the mkstemp temp file (tempfilename_/tempfile_) — SHAPE NOTE 2
  private chunks_: Uint8Array[] | null = null;
  private temppackid_: PackId = { id: 0, hash: new Uint8Array(16) };
  private ppackidUpdate_: PackId | null = null;
  private haveHash_ = false;

  // game/httppackmanager.cpp:14-22 — (the tempdir collapses with the temp file)
  constructor(service: HttpService | null, store: PackStore, cachedir = 'packs') {
    super(store, cachedir);
    this.service_ = service;
    this.Cancel();
  }

  // game/httppackmanager.cpp:24-26 — ~HttpPackManager
  Dispose(): void {
    this.Cancel();
  }

  // game/httppackmanager.cpp:28-73 — Install(const PackId *): the catalog
  // download (hash known up front)
  Install(ppackid: PackId, ctx: unknown, callback: ProgressCallback | null): boolean {
    // No service?
    if (this.service_ === null) return false;

    // Cancel any ongoing requests
    this.Cancel();

    // Remember what we're downloading
    this.temppackid_ = clonePackId(ppackid);
    this.ppackidUpdate_ = null;
    this.haveHash_ = true;

    // Create a temp file that will receive the bytes (mkstemp/fopen — the
    // failure arms collapse with the file system)
    this.chunks_ = [];

    // Start a new request
    this.ctx_ = ctx;
    this.callback_ = callback;
    this.req_ = this.service_.NewRequest(this);
    if (this.req_ === null) {
      this.chunks_ = null;
      return false;
    }
    this.SetServiceUrl(this.req_);

    // Submit the request. This will asynchronously call back.
    this.service_.SubmitRequest(this.req_);
    return true;
  }

  // game/httppackmanager.cpp:75-130 — Install(const char *pszURL): the custom
  // URL arm. The pack id is an XOR fold of md5(url); the content hash isn't
  // known yet (haveHash_ = false → FinishInstall adopts it and writes it back
  // through ppackidUpdate).
  InstallFromUrl(
    pszURL: string,
    ppackidUpdate: PackId | null,
    ctx: unknown,
    callback: ProgressCallback | null,
  ): boolean {
    // No service?
    if (this.service_ === null) return false;

    // Cancel any ongoing requests
    this.Cancel();

    // Remember what we're downloading - an url. Use a hash of the URL
    // for the pack id. We don't know the pack hash yet.
    this.temppackid_ = { id: 0, hash: new Uint8Array(16) };
    const hashURL = md5bytes(new TextEncoder().encode(pszURL));
    // *(dword *)&hashURL[0] ^ [4] ^ [8] ^ [12] — little-endian dword reads
    const dv = new DataView(hashURL.buffer, hashURL.byteOffset, 16);
    this.temppackid_.id =
      (dv.getUint32(0, true) ^
        dv.getUint32(4, true) ^
        dv.getUint32(8, true) ^
        dv.getUint32(12, true)) >>>
      0;

    // Since this is a custom pack, we don't have the pack hash yet.
    // Remember this so it can be calculated later.
    this.ppackidUpdate_ = ppackidUpdate;
    this.haveHash_ = false;

    // The temp file that will receive the bytes
    this.chunks_ = [];

    // Start a new request using the custom URL
    this.ctx_ = ctx;
    this.callback_ = callback;
    this.req_ = this.service_.NewRequest(this);
    if (this.req_ === null) {
      this.chunks_ = null;
      return false;
    }
    this.req_.SetURL(pszURL);

    // Submit the request. This will asynchronously call back.
    this.service_.SubmitRequest(this.req_);
    return true;
  }

  // game/httppackmanager.cpp:132-138 — "%s/%08x-%s?c=%d&v=%d"
  private SetServiceUrl(req: HttpRequest): void {
    const hash = ToHex(this.temppackid_.hash, 16);
    req.SetURL(
      `${kszPackUrl}/` +
        `${(this.temppackid_.id >>> 0).toString(16).padStart(8, '0')}-${hash}` +
        `?c=${kdwClientID}&v=${knVersionSimulation}`,
    );
  }

  // game/httppackmanager.cpp:140-159
  Cancel(): void {
    this.ppackidUpdate_ = null;
    this.callback_ = null;
    this.ctx_ = null;
    this.code_ = 0;
    this.error_ = false;
    if (this.req_ !== null) {
      if (this.service_ !== null) {
        this.service_.ReleaseRequest(this.req_);
      }
      this.req_ = null;
    }
    this.cbTotal_ = 0;
    this.cbLength_ = 0;
    // fclose + unlink the temp file
    this.chunks_ = null;
  }

  // game/httppackmanager.cpp:161-191
  OnReceivedResponse(preq: HttpRequest, code: number, pheaders: HeadersMap | null): void {
    this.code_ = code;
    if (code >= 400) {
      // Http error
      this.error_ = true;
      if (this.callback_ !== null) {
        this.callback_.OnError(this.ctx_, `Server returned error ${code}`);
      }
      return;
    }
    if (code >= 200 && code < 300) {
      // Success! Get Content-Length and call back
      this.cbLength_ = -1;
      const szLength = pheaders?.GetValue('Content-Length') ?? null;
      if (szLength !== null) {
        const n = ToInteger(szLength, 10);
        if (n !== null) this.cbLength_ = n;
      }
      if (this.callback_ !== null) {
        this.callback_.OnBegin(this.ctx_, this.cbLength_);
      }
      this.cbTotal_ = 0;
      return;
    }
    // Ignore other status codes. If it's a redirect, OnReceivedResponse
    // will get called again.
    return;
  }

  // game/httppackmanager.cpp:193-213 — fwrite into the temp file (the
  // out-of-space arm collapses with the file system)
  OnReceivedData(preq: HttpRequest, pbb: Uint8Array): void {
    if (this.error_) return;
    if (this.code_ >= 200 && this.code_ < 300) {
      this.chunks_!.push(pbb.slice());
      this.cbTotal_ += pbb.length;
      if (this.callback_ !== null) {
        this.callback_.OnProgress(this.ctx_, this.cbTotal_, this.cbLength_);
      }
    }
  }

  // game/httppackmanager.cpp:215-233
  OnFinishedLoading(_preq: HttpRequest): void {
    if (this.error_) {
      this.Cancel();
      return;
    }
    if (this.code_ >= 200 && this.code_ < 300) {
      if (this.FinishInstall()) {
        if (this.callback_ !== null) {
          this.callback_.OnFinished(this.ctx_);
        }
      } else {
        if (this.callback_ !== null) {
          this.error_ = true;
          this.callback_.OnError(this.ctx_, 'Downloaded mission pack is invalid!');
        }
      }
    }
    this.Cancel();
  }

  // game/httppackmanager.cpp:235-243
  OnError(preq: HttpRequest, pszError: string): void {
    if (this.error_) return;
    this.error_ = true;
    if (this.callback_ !== null) {
      this.callback_.OnError(this.ctx_, pszError);
    }
  }

  // game/httppackmanager.cpp:245-306 — validate the downloaded bytes (type tag
  // + md5), then install into the cache and the map
  private FinishInstall(): boolean {
    if (this.chunks_ === null) return false;

    // Assemble the temp file bytes
    let cb = 0;
    for (const c of this.chunks_) cb += c.length;
    const bytes = new Uint8Array(cb);
    let off = 0;
    for (const c of this.chunks_) {
      bytes.set(c, off);
      off += c.length;
    }

    // Make sure it is an addon pack (the 4-byte pdb type at offset 0x3c)
    let szType = '';
    for (let i = 0; i < 4; i++) szType += String.fromCharCode(bytes[0x3c + i] ?? 0);
    if (bytes.length < 0x3c + 4 || szType !== kszTypeAddon) {
      // fclose + unlink
      this.chunks_ = null;
      return false;
    }

    // Calculate the hash
    const hash = md5bytes(bytes);
    this.chunks_ = null;

    // Check that the hash matches, if we have it, otherwise use this hash!
    if (this.haveHash_) {
      let fMatch = true;
      for (let i = 0; i < 16; i++) {
        if (hash[i] !== this.temppackid_.hash[i]) {
          fMatch = false;
          break;
        }
      }
      if (!fMatch) {
        // unlink
        return false;
      }
    } else {
      this.temppackid_.hash.set(hash.subarray(0, 16));
      if (this.ppackidUpdate_ !== null) {
        this.ppackidUpdate_.id = this.temppackid_.id >>> 0;
        this.ppackidUpdate_.hash.set(this.temppackid_.hash.subarray(0, 16));
      }
    }

    // Hash matches. Now remove the pack with the same id, if there is one.
    this.Remove(this.temppackid_, false);

    // Install the pack into the cache (rename temp → GetPackFilepath). This
    // better not fail since the old pack has been removed already.
    this.store.write(PackManager.GetPackFilename(this.temppackid_), bytes);

    // Add this to the map
    this.map_.set(this.temppackid_.id >>> 0, clonePackId(this.temppackid_));
    return true;
  }
}
